import {
  collection,
  doc,
  getDocs,
  query,
  where,
  writeBatch,
  updateDoc,
  increment,
  serverTimestamp,
  type Unsubscribe,
} from '@firebase/firestore';
import { db } from './firebase';
import { onSnapshotWithRetry } from './snapshotRetry';
import { COLLECTIONS } from './collections';
import { PeopleList, Person } from '../types';
import { mapDocs } from '@utils/firestore';
import { PeopleListSchema, PersonSchema } from '../schemas/firestoreSchemas';
import { createPrefixedLogger } from '../utils/logger';

const log = createPrefixedLogger('api:peopleLists');

// Firestore allows 500 operations per batch; stay under it.
export const BATCH_SIZE = 450;

// Uploaded leader lists (Oct 2026). Private to the uploader: the rules deny
// everyone else, and no Cloud Function reads this collection. Emails stored
// here are display text only. Never copy one into a plan's partyContacts.

function listsCollection() {
  return collection(db, COLLECTIONS.PEOPLE_LISTS);
}
function listDoc(listId: string) {
  return doc(db, COLLECTIONS.PEOPLE_LISTS, listId);
}
function peopleCollection(listId: string) {
  return collection(db, COLLECTIONS.PEOPLE_LISTS, listId, COLLECTIONS.PEOPLE_LIST_PEOPLE);
}

/** The fields of one person as parsed from a file (see utils/peopleImport). */
export interface PersonInput {
  name: string;
  firstName?: string;
  lastName?: string;
  category?: string;
  organization?: string;
  suborganization?: string;
  title?: string;
  emails: string[];
  phone?: string;
  profileUrl?: string;
  note?: string;
  sourceRow: number;
  /** Set by "Add a person"; an uploaded row starts in Keep informed. */
  team?: boolean;
}

export interface CreateListInput {
  ownerUid: string;
  name: string;
  areaIds: string[];
  people: PersonInput[];
}

function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}

export const peopleListsApi = {
  newListId(): string {
    return doc(listsCollection()).id;
  },

  subscribeLists(
    uid: string,
    onNext: (lists: PeopleList[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    const q = query(listsCollection(), where('ownerUid', '==', uid));
    const confirmed = new Set<string>();
    return onSnapshotWithRetry(
      q,
      (snap) => {
        // A just-created list shows up here from the local cache before the
        // server has it. Subscribing to its people then is denied (the
        // people rule reads the parent list on the server) and the listener
        // never recovers, so hold a list back until the server confirms it.
        // The confirmation arrives as its own snapshot, because createdAt is
        // a server timestamp. Once confirmed, a list stays shown, so a rename
        // (also briefly pending) doesn't make it blink out.
        for (const d of snap.docs) if (!d.metadata.hasPendingWrites) confirmed.add(d.id);
        const lists = mapDocs(snap, PeopleListSchema).filter((l) => confirmed.has(l.id));
        lists.sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis());
        onNext(lists);
      },
      (error) => {
        log.error('subscribeLists failed:', error);
        onError?.(error);
      }
    );
  },

  subscribePeople(
    listId: string,
    onNext: (people: Person[]) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    return onSnapshotWithRetry(
      query(peopleCollection(listId)),
      (snap) => {
        const people = mapDocs(snap, PersonSchema);
        people.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        onNext(people);
      },
      (error) => {
        log.error('subscribePeople failed:', error);
        onError?.(error);
      }
    );
  },

  // The list doc goes in its own batch first: the people rules read the
  // parent's ownerUid with get(), which sees the state before a batch, so
  // the list has to exist before its people can be written. If a later
  // chunk fails, the half-written list is removed before the error goes up.
  async createList(
    listId: string,
    input: CreateListInput,
    onProgress?: (written: number, total: number) => void
  ): Promise<void> {
    const { ownerUid, name, areaIds, people } = input;
    const head = writeBatch(db);
    head.set(listDoc(listId), {
      ownerUid,
      name,
      areaIds,
      rowCount: people.length,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    await head.commit();
    onProgress?.(0, people.length);
    try {
      for (let i = 0; i < people.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        for (const person of people.slice(i, i + BATCH_SIZE)) {
          batch.set(
            doc(peopleCollection(listId)),
            stripUndefined({ ...person, team: person.team ?? false })
          );
        }
        await batch.commit();
        onProgress?.(Math.min(i + BATCH_SIZE, people.length), people.length);
      }
    } catch (error) {
      log.warn('createList failed part way, removing the list:', error);
      await peopleListsApi.deleteList(listId).catch(() => {});
      throw error;
    }
  },

  // One person onto an existing list. The person and the list's rowCount go in
  // one batch; the people rules read the list with get(), which is fine here
  // because the list already exists.
  async addPerson(listId: string, person: PersonInput): Promise<void> {
    const batch = writeBatch(db);
    batch.set(
      doc(peopleCollection(listId)),
      stripUndefined({ ...person, team: person.team ?? false })
    );
    batch.update(listDoc(listId), { rowCount: increment(1), updatedAt: serverTimestamp() });
    await batch.commit();
  },

  async setTeam(listId: string, personId: string, team: boolean): Promise<void> {
    await updateDoc(doc(peopleCollection(listId), personId), { team });
  },

  async renameList(listId: string, name: string): Promise<void> {
    await updateDoc(listDoc(listId), { name, updatedAt: serverTimestamp() });
  },

  // People first, then the list: the people rules need the list to exist.
  async deleteList(listId: string): Promise<void> {
    const snap = await getDocs(peopleCollection(listId));
    for (let i = 0; i < snap.docs.length; i += BATCH_SIZE) {
      const batch = writeBatch(db);
      for (const d of snap.docs.slice(i, i + BATCH_SIZE)) batch.delete(d.ref);
      await batch.commit();
    }
    const last = writeBatch(db);
    last.delete(listDoc(listId));
    await last.commit();
  },
};
